import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, Input, Label, FormGroup, Button } from "reactstrap";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { useGetCustomerDeposits, useUpdateCustomerDeposit } from "../../../api/customer-deposits/service";

const CustomerRefundDeposit = ({
  activeColor,
  activeColorStyle,
  isLightTheme,
  customerId,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [comment, setComment] = useState("");
  const updateDepositMutation = useUpdateCustomerDeposit();

  const { data: depositsData, isLoading, isError } = useGetCustomerDeposits(
    customerId,
    !!customerId
  );
  // API returns { results: Array, pagination: {...} }
  const deposits = useMemo(() => {
    if (!depositsData) return [];

    // If depositsData is already an array, return it
    if (Array.isArray(depositsData)) return depositsData;

    // If depositsData has a results property that is an array, return it
    if (depositsData.results && Array.isArray(depositsData.results)) return depositsData.results;

    // If depositsData has a data property that is an array, return it
    if (depositsData.data && Array.isArray(depositsData.data)) return depositsData.data;

    // Otherwise return empty array
    return [];
  }, [depositsData]);

  // Clear form after successful refund
  useEffect(() => {
    if (updateDepositMutation.isSuccess) {
      setSelectedDeposit(null);
      setComment("");
    }
  }, [updateDepositMutation.isSuccess]);

  const handleRefund = () => {
    if (!selectedDeposit || !selectedDeposit.tran_id) {
      return;
    }

    const updateData = {
      comment: comment || undefined,
    };

    updateDepositMutation.mutate({
      tranId: selectedDeposit.tran_id,
      data: updateData,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100%"
    }}>
      {/* Refund Button - Right after header divider */}
      <div style={{
        marginBottom: 16,
        display: "flex",
        justifyContent: "flex-end"
      }}>
        <Button
          onClick={handleRefund}
          disabled={!selectedDeposit || !selectedDeposit.tran_id || updateDepositMutation.isPending}
          style={{
            ...activeColorStyle,
            color: "white",
            padding: "8px 16px",
            fontWeight: 600,
            fontSize: 14,
            opacity: !selectedDeposit || !selectedDeposit.tran_id || updateDepositMutation.isPending ? 0.5 : 1,
            cursor: !selectedDeposit || !selectedDeposit.tran_id || updateDepositMutation.isPending ? "not-allowed" : "pointer"
          }}
        >
          {updateDepositMutation.isPending
            ? t("customerDetail.refundDeposit.processing") || "Processing..."
            : t("customerDetail.refundDeposit.refund")}
        </Button>
      </div>

      <h6 style={{
        marginBottom: 16,
        color: isLightTheme ? "#1e1e2f" : "#ffffff",
        fontWeight: 600
      }}>
        {t("customerDetail.refundDeposit.title")}
      </h6>

      <div style={{ marginBottom: 16 }}>
        <Table
          responsive
          style={{
            backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
            color: isLightTheme ? "#1e1e2f" : "#ffffff"
          }}
        >
          <thead>
            <tr>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.id")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.postDate")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.tranCode")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.amount")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.form")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.salesLocation")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.comment")}</th>
              <th style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>{t("customerDetail.refundDeposit.select")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 20, fontSize: 12 }}>
                  {t("customerDetail.refundDeposit.loading")}
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 20, fontSize: 12, color: "#ff5c5c" }}>
                  {t("customerDetail.refundDeposit.error")}
                </td>
              </tr>
            ) : deposits.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 20, fontSize: 12 }}>
                  {t("customerDetail.refundDeposit.noDeposits")}
                </td>
              </tr>
            ) : (
              deposits.map((deposit) => (
                <tr key={deposit.id || deposit.tran_id}>
                  <td style={{ fontSize: 12 }}>{deposit.tran_id || deposit.id || "-"}</td>
                  <td style={{ fontSize: 12 }}>
                    {deposit.post_date
                      ? formatDateForDisplay(deposit.post_date)
                      : deposit.postDate
                        ? formatDateForDisplay(deposit.postDate)
                        : deposit.date
                          ? formatDateForDisplay(deposit.date)
                          : "-"}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {deposit.tran_id || deposit.tran_code || deposit.tranCode || deposit.transaction_code || "-"}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {deposit.amount_received ? formatCurrency(deposit.amount_received) :
                      deposit.amount_paying ? formatCurrency(deposit.amount_paying) :
                        deposit.amount ? formatCurrency(deposit.amount) : "-"}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {deposit.payment_method || deposit.form || deposit.payment_form || deposit.paymentForm || "-"}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {deposit.sales_location?.channel?.name ||
                      deposit.sales_location?.name ||
                      deposit.sales_location ||
                      deposit.salesLocation ||
                      deposit.location || "-"}
                  </td>
                  <td style={{ fontSize: 12 }}>{deposit.comment || deposit.ref_number || ""}</td>
                  <td style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="depositSelect"
                      checked={selectedDeposit?.id === deposit.id}
                      onChange={() => setSelectedDeposit(deposit)}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <FormGroup style={{ marginBottom: 24 }}>
        <Label
          style={{
            fontSize: 12,
            color: isLightTheme ? "#666" : "#a0a0b2",
            marginBottom: 8
          }}
        >
          {t("customerDetail.refundDeposit.comment")}
        </Label>
        <Input
          type="textarea"
          rows="3"
          className={isTheme2 ? "form-control" : undefined}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("customerDetail.refundDeposit.enterComment")}
          style={
            isTheme2
              ? {
                  backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                }
              : {
                  backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  border: `1px solid ${
                    isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                  }`,
                }
          }
        />
      </FormGroup>
    </div>
  );
};

export default CustomerRefundDeposit;

